import React from 'react';

const UserProfilePic = ({ user, className = "w-10 h-10", altText = "User profile" }) => {
  // Helper function to get profile picture URL (restored comprehensive logic)
  const getProfilePicUrl = () => {
    if (!user) return null;

    // First check the user parameter directly for profilePic as an array
    if (user.profilePic && Array.isArray(user.profilePic) && user.profilePic.length > 0 && user.profilePic[0]?.url) {
      return user.profilePic[0].url.startsWith('http') ? user.profilePic[0].url : `${user.profilePic[0].url}`;
    }
    // Check for profilePic as a direct object (e.g., from customerProfile)
    if (user.profilePic && typeof user.profilePic === 'object' && !Array.isArray(user.profilePic) && user.profilePic.url) {
      return user.profilePic.url.startsWith('http') ? user.profilePic.url : `${user.profilePic.url}`;
    }

    // Check user.user nested structure (common in auth responses)
    if (user.user?.profilePic && Array.isArray(user.user.profilePic) && user.user.profilePic.length > 0 && user.user.profilePic[0]?.url) {
      return user.user.profilePic[0].url.startsWith('http') ? user.user.profilePic[0].url : `${user.user.profilePic[0].url}`;
    }
    // Check user.user.profilePic as a direct object
    if (user.user?.profilePic && typeof user.user.profilePic === 'object' && !Array.isArray(user.user.profilePic) && user.user.profilePic.url) {
      return user.user.profilePic.url.startsWith('http') ? user.user.profilePic.url : `${user.user.profilePic.url}`;
    }

    // Check if user prop itself is an array (e.g., from some Strapi relations)
    if (Array.isArray(user) && user[0]?.profilePic && Array.isArray(user[0].profilePic) && user[0].profilePic.length > 0 && user[0].profilePic[0]?.url) {
      return user[0].profilePic[0].url.startsWith('http') ? user[0].profilePic[0].url : `${user[0].profilePic[0].url}`;
    }
    // Check if user prop itself is an array and profilePic is a direct object
    if (Array.isArray(user) && user[0]?.profilePic && typeof user[0].profilePic === 'object' && !Array.isArray(user[0].profilePic) && user[0].profilePic.url) {
      return user[0].profilePic.url.startsWith('http') ? user[0].profilePic.url : `${user[0].profilePic.url}`;
    }

    // Fallback for other common avatar field names
    if (user.avatar) {
      const avatarUrl = typeof user.avatar === 'string' ? user.avatar : (user.avatar.url || user.avatar[0]?.url);
      if (avatarUrl) {
        return avatarUrl.startsWith('http') ? avatarUrl : `${avatarUrl}`;
      }
    }
    if (user.image) {
      const imageUrl = typeof user.image === 'string' ? user.image : (user.image.url || user.image[0]?.url);
      if (imageUrl) {
        return imageUrl.startsWith('http') ? imageUrl : `${imageUrl}`;
      }
    }

    // Check for social login picture URLs (e.g. from Google)
    if (user.ProfilePicUrl && typeof user.ProfilePicUrl === 'string') {
      return user.ProfilePicUrl;
    }
    if (user.user?.ProfilePicUrl && typeof user.user.ProfilePicUrl === 'string') {
      return user.user.ProfilePicUrl;
    }
    if (user.profilePicUrl && typeof user.profilePicUrl === 'string') {
      return user.profilePicUrl;
    }
    if (user.user?.profilePicUrl && typeof user.user.profilePicUrl === 'string') {
      return user.user.profilePicUrl;
    }

    return null;
  };

  // Get display name from user param (restored comprehensive logic)
  const getDisplayName = () => {
    if (!user) return 'User';
    if (Array.isArray(user)) {
      return user[0]?.username || user[0]?.firstname || 'User';
    }
    return user.username || user.firstname || user.user?.username || user.user?.firstname || 'User';
  };

  const profilePicUrl = getProfilePicUrl();
  const displayName = getDisplayName();
  const initial = displayName ? displayName.charAt(0).toUpperCase() : 'U'; // Ensure initial has a fallback

  // Refactored onError to be a separate function for clarity
  const handleImageError = (e) => {
    e.target.onerror = null; // Prevent looping
    e.target.style.display = 'none'; // Hide the broken image
    const parent = e.target.parentNode;
    if (parent) {
      // Check if fallback already exists to prevent duplicates
      if (parent.querySelector('.user-initial-fallback')) return;

      const fallbackDiv = document.createElement('div');
      // Ensure all classes from the img are transferred, plus specific fallback styles
      fallbackDiv.className = `${className} rounded-full bg-brand-teal-dark flex items-center justify-center text-white font-bold border-2 border-white shadow-sm user-initial-fallback`;
      fallbackDiv.textContent = initial;
      parent.appendChild(fallbackDiv);
    }
  };

  if (profilePicUrl) {
    return (
      // Wrap img in a div to ensure parentNode for onError fallback always exists
      <div className="user-profile-pic-wrapper" style={{ display: 'inline-block', width: className.match(/w-[^\s]+/)?.[0] || 'auto', height: className.match(/h-[^\s]+/)?.[0] || 'auto' }}>
        <img
          src={profilePicUrl}
          alt={altText || `${displayName}'s profile picture`}
          className={`${className} rounded-full object-cover border-2 border-white shadow-sm`}
          onError={handleImageError}
          referrerPolicy="no-referrer"
        />
      </div>
    );
  }

  // Fallback to initial if no URL
  return (
    <div className={`${className} rounded-full bg-brand-teal-dark flex items-center justify-center text-white font-bold border-2 border-white shadow-sm user-initial-fallback`}>
      {initial}
    </div>
  );
};

export default UserProfilePic; 